/* globals window, CSS, requestAnimationFrame */

import React, { useEffect, useRef } from 'react';
import { css, useTheme } from '@emotion/react';
import { breakpoints } from '@birdi/theme';
import MapLibreGL from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

interface MapComponentProps {
  maps: { center: [number, number]; id: string; name: string }[];
}

function clusterMaps(maps: { center: [number, number]; id: string; name: string }[], distance = 0.5) {
  // Simple grid-based clustering by lat/lon
  const clusters: { lat: number; lon: number; count: number; ids: string[]; names: string[] }[] = [];
  maps.forEach((map) => {
    const [lon, lat] = map.center;
    let found = false;
    for (const cluster of clusters) {
      if (Math.abs(cluster.lat - lat) < distance && Math.abs(cluster.lon - lon) < distance) {
        // Add to cluster
        cluster.lat = (cluster.lat * cluster.count + lat) / (cluster.count + 1);
        cluster.lon = (cluster.lon * cluster.count + lon) / (cluster.count + 1);
        cluster.count++;
        cluster.ids.push(map.id);
        cluster.names.push(map.name);
        found = true;
        break;
      }
    }
    if (!found) {
      clusters.push({ lat, lon, count: 1, ids: [map.id], names: [map.name] });
    }
  });
  return clusters;
}

function addMarkersToMap(
  map: MapLibreGL.Map,
  maps: { center: [number, number]; id: string; name: string }[],
): MapLibreGL.Marker[] {
  const clusters = clusterMaps(maps);
  const markers: MapLibreGL.Marker[] = [];
  clusters.forEach((cluster) => {
    const el = document.createElement('div');
    el.style.background = '#0078C0';
    el.style.color = '#fff';
    el.style.borderRadius = '50%';
    el.style.width = '28px';
    el.style.height = '28px';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.style.fontWeight = '400';
    el.style.fontSize = '1em';
    el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
    el.innerText = String(cluster.count);
    el.style.cursor = 'pointer';
    el.onclick = () => {
      if (map.getZoom() < 10) {
        map.flyTo({
          center: [cluster.lon, cluster.lat],
          zoom: Math.min(map.getZoom() + 1, 10),
          essential: true,
        });
      }
    };
    const marker = new MapLibreGL.Marker({ element: el })
      .setLngLat([cluster.lon, cluster.lat])
      .addTo(map);
    markers.push(marker);
  });
  return markers;
}

export const MapComponent: React.FC<MapComponentProps> = ({ maps }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreGL.Map | null>(null);
  const markersRef = useRef<MapLibreGL.Marker[]>([]);

  // Create map once on mount; remove only on unmount
  useEffect(() => {
    if (!mapContainer.current) return;
    const map = new MapLibreGL.Map({
      container: mapContainer.current,
      style: 'https://demotiles.maplibre.org/style.json',
      center: [60, 0],
      zoom: 1,
      minZoom: 1,
      renderWorldCopies: false,
      attributionControl: false,
    });
    mapRef.current = map;
    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Update markers when maps change; do not recreate the map (preserves zoom/pan)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = addMarkersToMap(map, maps);
  }, [maps]);

  return (
    <div
      ref={mapContainer}
      css={css`
        width: 100%;
        height: 300px;
        border-radius: 5px;
        overflow: hidden;
        min-height: 220px;
        opacity: 0.8;
        @media (max-width: 600px) {
          height: 200px;
        }
      `}
    />
  );
};
